import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';

import { ActionData, Privacy } from '@bonusly/common-action-input';
import { useHttpClient } from '@bonusly/http-client';
import { ReasonToken } from '@bonusly/recipient-entities';
import { enabled, track } from '@bonusly/who-am-i';

import { createBonusData } from '../../../../../../../components/recognition/give-box';
import { Award, Bonus, CachedBonuses } from '../../../../../../../types/bonus';
import { useToast } from '../../../../../hooks/useToast';
import { ResourcePath } from '../../../api/hooks/ResourcePath';
import { QueryKey } from '../../../api/hooks/types';
import { useInvalidateApprovalRequests } from '../../../cache/invalidators/approvalRequests';
import { useInvalidateAwards } from '../../../cache/invalidators/awards';
import { addBonusToFeed } from '../../../cache/mutators/bonus';

interface UseGiveAwardConfig {
	award: Award;
	hideGiver: boolean;
	isTemplatedAward: boolean;
	onError?: (error: unknown) => void;
	onSuccess: VoidFunction;
}

export const useGiveAward = ({ award, hideGiver, isTemplatedAward, onError, onSuccess }: UseGiveAwardConfig) => {
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');
	const { createSuccessToast } = useToast();
	const { t } = useTranslation('recognition');

	const invalidateApprovalRequests = useInvalidateApprovalRequests();
	const invalidateAwards = useInvalidateAwards();

	// in order to simplify the bonus data, inject the hide_giver flag into the award
	award.hide_giver = hideGiver;

	const { mutate, isLoading: isSubmitting } = useMutation<AxiosResponse<{ result: Bonus }>, unknown, ActionData, unknown>(
		(action) => {
			const isAwardsRedesignEnabled = enabled('awards-redesign');
			const body = isAwardsRedesignEnabled ? buildFormBody(award, action) : { bonus: createBonusData({ action, award }) };

			return http.post<{ result: Bonus }>(ResourcePath.Bonuses, body, {
				params: { include_children: true },
			});
		},
		{
			onError: (err: unknown) => {
				onError?.(err);
			},
			onSuccess: ({ data: { result: bonus } }, vars) => {
				if (award?.approval_process) {
					invalidateApprovalRequests();
				} else {
					queryClient.setQueriesData<CachedBonuses>([QueryKey.BonusesInfinite], (cached) => addBonusToFeed(bonus, cached));
				}

				createSuccessToast(
					t('awards.feedback.success', {
						context: award.approval_process ? 'approval' : 'no_approval',
						defaultValue: 'awards.feedback.success',
					})
				);

				track('Award Give Complete', {
					award_name: award.reason,
					award_type: isTemplatedAward ? 'Templated' : 'Non Templated',
					budget_timeframe: award.budget_period,
					recipient_count: vars.recipients.length,
					amount: vars.amount,
					hashtag_included: vars.hasHashtag,
					hide_giver: award.hide_giver,
					gif_included: vars.attachment?.category === 'social',
					image_included: vars.attachment?.category === 'image',
					video_included: vars.attachment?.category === 'video',
					tag_included: vars.hasHashtag,
					result: 'Success',
					reason: vars.text,
				});

				invalidateAwards();
				onSuccess();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};

interface AwardData {
	attachments_attributes?: Record<string, any>;
	award_id: string;
	giver_id: string;
	hide_giver: boolean;
	privacy: Privacy;
	reason: string;
	reason_tokens: ReasonToken[];
}

const LEGACY_USERNAME_KEY = '@receiver_username'; // See BonusTemplate::LEGACY_PLACEHOLDER_RECIPIENT
const USERNAME_KEY = '@recipient'; // See BonusTemplate::PLACEHOLDER_RECIPIENT

const buildFormBody = (award: Award, action: ActionData) => {
	const recipients = action.recipients.filter((recipient) => recipient.id);

	let reason = award.reason;

	if (award.reason_editable) {
		reason = action.text;

		if (!award.bonus_amount_changeable) {
			reason = `+${award.amount} ${reason}`;
		}
	} else {
		const usernames = action.recipients.map((recipient) => `@${recipient.username}`).join(' ');
		reason = reason.replace(LEGACY_USERNAME_KEY, 'USERS_PLACEHOLDER').replace(USERNAME_KEY, 'USERS_PLACEHOLDER').replace('USERS_PLACEHOLDER', usernames);

		if (award.bonus_amount_changeable) {
			reason = `+${action.amount} ${reason}`;
		}
	}

	const data: AwardData = {
		award_id: award.id,
		attachments_attributes: action.attachment ? [action.attachment.toJSON()] : undefined,
		giver_id: award.giver?.id,
		hide_giver: !!award.hide_giver,
		privacy: action.privacy,
		reason,
		reason_tokens: recipients.map(({ id, dimension, displayName, type = 'username', username, userIds }) => ({
			context: 'recipient',
			token: `@${username}`,
			display_name: displayName,
			id: type === 'group' ? id : undefined,
			type: (type === 'group' ? dimension : type) as ReasonToken['type'],
			user_ids: type === 'group' ? userIds : [id],
		})),
	};

	return { bonus: data };
};
